
import { defineComponent, ref, toRefs, computed, reactive, PropType, watch } from "vue";

import { ElDialog, ElForm, ElFormItem, ElInput, ElButton, vLoading, ElMessage } from "element-plus";

import { StyleInput } from "@/components/common";
import { AlbumId, AlbumModel, ArtistId, ArtistModel } from "@/typings";

import EnumEditor from "./Fields/Enum.vue";

import { online_links_default, online_link_name } from "@/basics/enums";
import {
    doCombineAlbum,
    doCombineThird,
    doEditAlbum,
    getMetaEditAlbum,
    getMetaInfo,
} from "@/services";
import { useRouter } from "vue-router";

function parseDiscogsUrl(url: string) {
    // 创建一个URL对象
    const urlObj = new URL(url);

    // 确保主机名是discogs.com
    if (urlObj.hostname !== 'www.discogs.com') {
        return null;
    }

    // 使用正则表达式解析路径
    const pathRegex = /^\/(release|master)\/(\d+)-/;
    const match = urlObj.pathname.match(pathRegex);

    if (match) {
        const type = match[1]; // release 或 master
        const id = match[2];   // 数字ID

        return {
            site: 'discogs',
            type: type,
            id: id
        };
    } else {
        throw new Error('URL格式不正确');
    }
}

export default defineComponent({
    directives: {
        vLoading,
    },
    components: {
        ElDialog,
        ElForm,
        ElFormItem,
        ElInput,
        ElButton,
        StyleInput,
        EnumEditor,
    },
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        isKeepUrl: {
            type: Boolean,
            default: false,
        },
        artist: {
            type: String as unknown as PropType<ArtistId>,
            default: "",
        },
        id: {
            type: String as unknown as PropType<AlbumId>,
            default: "",
        },
    },
    emits: {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        "update:visible"(val: boolean) {
            return true;
        },
        change() {
            return true;
        },
    },
    setup(props, { emit }) {
        const { visible, id, isKeepUrl, artist } = toRefs(props);

        const router = useRouter();

        const loadings = reactive({
            formLoading: false,
        });

        const dialogFormVisible = computed({
            get: () => visible.value,
            set: (val) => emit("update:visible", val),
        });

        const dialogTitle = ref("");
        const isCombine = ref(0);
        const handleCombine = (val: number) => {
            isCombine.value = val;
        };

        const everEdited = ref(false);
        const form_edited = () => {
            everEdited.value = true;
        };
        const url_form_edited = () => {
            everEdited.value = true;
            const url = parseDiscogsUrl(combineId.value);
            if (url) {
                if (url.site === 'discogs') {
                    if (url.type === 'release') {
                        combineIdFrom.value.index = 'release';
                    } else {
                        combineIdFrom.value.index = 'master';
                    }
                    combineId.value = url.id;
                }
            }
        };

        const formEditAlbum = reactive<AlbumModel>({
            title: "",
            title_roman: "",
            title_cn_simp: "",
            styles: [],
            relate_styles: [],
            links: [],
            online_links: {} as any,
            primary_img: "",
            main_artist: {
                _id: "",
                name: "n/a",
            } as ArtistModel,
            main_artist_show: "",
            artists: [],
            type: {
                index: 1,
                name: "全长",
            },
            album_type: {
                index: 0,
                name: "录音室专辑",
            },
        } as any as AlbumModel);

        const combineId = ref("");

        // //很奇怪，这里用工具看明明已经变了
        const combineIdFrom = ref({
            index: "master",
            name: "",
        });

        const changeMainArtist = () => {
            getMetaInfo("artist", formEditAlbum.main_artist._id)
                .then((json) => {
                    formEditAlbum.main_artist = {
                        name: json.data.info.name,
                        _id: json.data.info._id,
                    } as ArtistModel;
                    formEditAlbum.main_artist_show = json.data.info.name;
                    form_edited();
                })
                .catch((json) => {
                    ElMessage.error(json.data.msg);
                });
        };

        const removeArtistsIndex = (index: number) => {
            formEditAlbum.artists.splice(index, 1);
            form_edited();
        };

        const addArtistId = ref("" as ArtistId);
        const addArtists = () => {
            getMetaInfo("artist", addArtistId.value)
                .then((json) => {
                    formEditAlbum.artists.push({
                        name: json.data.info.name,
                        _id: json.data.info._id,
                    } as ArtistModel);
                    addArtistId.value = "" as ArtistId;
                })
                .catch((json) => {
                    ElMessage.error(json.data.msg);
                });
        };

        const onChooseStyle = (styles: any) => {
            formEditAlbum.styles = styles;
            form_edited();
        };

        const choose_relate_style = (styles: any) => {
            formEditAlbum.relate_styles = styles;
            form_edited();
        };

        const change_online_link = () => {
            form_edited();
        };

        const doConfirmEdit = () => {
            if (!everEdited.value) {
                ElMessage.warning("数据无变化");
                return;
            }
            loadings.formLoading = true;

            if (isCombine.value === 1) {
                doCombineAlbum(id.value, {
                    combineId: combineId.value,
                })
                    .then(() => {
                        ElMessage.success("感谢您的编辑");
                        loadings.formLoading = false;
                        dialogFormVisible.value = false;
                        router.replace({
                            path: "/meta/album/" + combineId.value,
                        });
                    })
                    .catch((json) => {
                        ElMessage.error(json.data.msg);
                    })
                    .finally(() => {
                        loadings.formLoading = false;
                    });
            } else if (isCombine.value === 2) {
                // combineIdFrom.index 的话，在这里，居然还是旧的值

                doCombineThird(id.value, {
                    combineId: combineId.value,
                    combineIdFrom: combineIdFrom.value.index,
                    artistId: formEditAlbum.main_artist._id,
                })
                    .then(() => {
                        ElMessage.success("感谢您的编辑");
                        dialogFormVisible.value = false;
                        emit("change");
                        if (isKeepUrl.value) {
                            router.replace({
                                path: "/meta/album/" + id.value,
                            });
                        }
                    })
                    .catch((json) => {
                        ElMessage.error(json.data.msg);
                    })
                    .finally(() => {
                        loadings.formLoading = false;
                    });
            } else {
                doEditAlbum(id.value, formEditAlbum).then((json) => {
                    Object.assign(formEditAlbum, json.data.info);

                    ElMessage.success("感谢您的编辑");
                    loadings.formLoading = false;
                    emit("change");
                    dialogFormVisible.value = false;
                    if (isKeepUrl.value) {
                        router.replace({
                            path: "/meta/album/" + json.data.info._id,
                        });
                    }
                });
            }
        };

        const initFormData = (albumInfo: AlbumModel) => {
            dialogTitle.value = albumInfo.main_artist.name + " - " + albumInfo.title;

            if (Array.isArray(albumInfo.online_links)) {
                albumInfo.online_links = JSON.parse(JSON.stringify(online_links_default));
            } else {
                // TODO type
                for (const k in online_links_default) {
                    if (!(albumInfo as any).online_links[k]) {
                        (albumInfo as any).online_links[k] = JSON.parse(
                            JSON.stringify((online_links_default as any)[k]),
                        );
                    }
                }
            }

            // TODO type
            try {
                albumInfo.links = albumInfo.links.map((link: any) => {
                    if (link._id && link._id.$oid) {
                        link._id = link._id.$oid;
                    }
                    return link;
                });
            } catch (e) {
                console.error(e);
            }
            Object.assign(formEditAlbum, albumInfo);
        };

        const refreshData = () => {
            Object.assign(formEditAlbum, {
                title: "",
                title_roman: "",
                title_cn_simp: "",
                styles: [],
                relate_styles: [],
                links: [],
                online_links: {} as any,
                primary_img: "",
                main_artist: {
                    _id: "",
                    name: "n/a",
                } as ArtistModel,
                main_artist_show: "",
                artists: [],
                type: {
                    index: 1,
                    name: "全长",
                },
                album_type: {
                    index: 0,
                    name: "录音室专辑",
                },
                year: 0,
            });

            if (id.value === "") {
                formEditAlbum.artists = [];
                if (artist.value) {
                    formEditAlbum.main_artist._id = artist.value;
                    addArtistId.value = artist.value;
                    changeMainArtist();
                    addArtists();
                }
                initFormData(formEditAlbum);
                return;
            }
            loadings.formLoading = true;

            getMetaEditAlbum(id.value)
                .then((json) => {
                    initFormData(json.data.info);
                })
                .finally(() => {
                    loadings.formLoading = false;
                });
        };

        watch(
            () => {
                return visible.value;
            },
            refreshData,
            {
                immediate: false,
            },
        );

        return {
            loadings,
            dialogFormVisible,
            dialogTitle,
            isCombine,
            handleCombine,
            combineId,
            combineIdFrom,
            everEdited,
            form_edited,
            url_form_edited,
            formEditAlbum,

            online_link_name,
            changeMainArtist,
            removeArtistsIndex,
            addArtistId,
            addArtists,
            onChooseStyle,
            choose_relate_style,
            change_online_link,
            doConfirmEdit,
        };
    },
});
